import React, { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import AddIcon from '@atlaskit/icon/core/add';
import Button from '@atlaskit/button/new';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';

import { messages } from '../../messages';
import type { FieldMetadata } from '../../__types__/apiUtils';
import type { UnsavedColumnToFieldMapping } from '../../providers/BulkConfigureContextProvider';
import {
	useIsConfigureLoading,
	useMappingsActions,
	useMappingsState,
} from '../../providers/BulkConfigureContextProvider';
import { useIssueCreateSidePanelContext } from '../../providers/IssueCreateSidePanelContextProvider';

import { ColumnToIssueFieldMapping } from './ColumnToIssueFieldMapping';

const mapFieldsToOptions = (fields: FieldMetadata[]) => {
	return fields.map((field) => ({
		label: field.name,
		value: field,
	}));
};

type MappingValidationError = {
	message: string;
};

const createIssueFieldMappingKey = (mapping: UnsavedColumnToFieldMapping, index: number) => {
	return `${index}-${mapping.column?.index ?? 'null'}-${mapping.field?.fieldId ?? 'null'}`;
};

export const ColumnToIssueFieldMappingView = forwardRef<{ validateMappings: () => void }, {}>(
	(_, ref) => {
		const { formatMessage } = useIntl();
		const [{ pageMode }] = useIssueCreateSidePanelContext();
		const { isLoading: isConfigureLoading } = useIsConfigureLoading();
		const {
			optionsColumns,
			optionsIssueTypeFields,
			unsavedColumnToFieldMappings,
			loadingIssueTypeFields,
		} = useMappingsState();
		const { setUnsavedColumnToFieldMappings, addUnsavedColumnToFieldMapping } =
			useMappingsActions();
		const tableColumnOptions = optionsColumns
			? optionsColumns.map((column) => ({ label: column.header, value: column }))
			: [];
		const issueFieldOptions = optionsIssueTypeFields
			? mapFieldsToOptions(optionsIssueTypeFields)
			: [];
		const isLoading = isConfigureLoading || loadingIssueTypeFields;
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const handleMappingChange = useCallback(
			(index: number, newMapping: UnsavedColumnToFieldMapping) => {
				if (unsavedColumnToFieldMappings) {
					const updatedMappings = unsavedColumnToFieldMappings.map((mapping, idx) =>
						idx === index ? newMapping : mapping,
					);
					setUnsavedColumnToFieldMappings(updatedMappings);
				}
			},
			[unsavedColumnToFieldMappings, setUnsavedColumnToFieldMappings],
		);

		// Create a Set of mapped column indices for quick lookup
		const mappedColumnIndices = useMemo(() => {
			if (!unsavedColumnToFieldMappings) {
				return new Set<number>();
			}
			return new Set(
				unsavedColumnToFieldMappings
					.map((mapping) => mapping.column?.index)
					.filter((index) => index !== undefined) as number[],
			);
		}, [unsavedColumnToFieldMappings]);

		// Find the next available column that is not already mapped (or undefined if none exists)
		const nextAvailableColumn = useMemo(() => {
			if (!optionsColumns) {
				return undefined;
			}

			// Find the first unmapped column starting from index 0
			for (const column of optionsColumns) {
				if (!mappedColumnIndices.has(column.index)) {
					return column;
				}
			}

			return undefined; // No available column found
		}, [optionsColumns, mappedColumnIndices]);

		const addMapping = useCallback(() => {
			if (isLoading) {
				return;
			}
			const newMapping: UnsavedColumnToFieldMapping = {
				column: nextAvailableColumn,
				field: undefined,
				errors: {},
				isDefaultMapping: false,
			};
			addUnsavedColumnToFieldMapping(newMapping);
		}, [isLoading, addUnsavedColumnToFieldMapping, nextAvailableColumn]);

		const fireAddMappingAnalytics = useCallback(() => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'confluenceHighlightActionsMenu',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'addMapping',
					type: 'ui',
					attributes: {
						pageMode,
					},
				},
			}).fire();
		}, [createAnalyticsEvent, pageMode]);

		const handleAddMappingClick = useCallback(() => {
			fireAddMappingAnalytics();
			addMapping();
		}, [fireAddMappingAnalytics, addMapping]);

		// TODO: Please update the messages and ensure they are internationalized once the content design has been finalized.
		const validateMappings = (): boolean => {
			if (!unsavedColumnToFieldMappings) {
				return false;
			}
			let isValid = true;
			const mappedField = new Set<string>();
			const updatedMappings = unsavedColumnToFieldMappings.map((mapping) => {
				const { column, field } = mapping;
				const errors: { column?: MappingValidationError; field?: MappingValidationError } = {};
				if (!column) {
					errors.column = {
						message: formatMessage(messages.bulkConfigureMapTableToIssueErrorColumnNotSelected),
					};
					isValid = false;
				}
				if (!field) {
					errors.field = {
						message: formatMessage(messages.bulkConfigureMapTableToIssueErrorFieldNotSelected),
					};
					isValid = false;
				} else {
					const fieldId = field.fieldId;
					if (mappedField.has(fieldId)) {
						errors.field = {
							message: formatMessage(messages.bulkConfigureMapTableToIssueErrorFieldAlreadyMapped),
						};
						isValid = false;
					} else {
						mappedField.add(fieldId);
					}
				}
				return {
					...mapping,
					errors,
				};
			});
			setUnsavedColumnToFieldMappings(updatedMappings);
			return isValid;
		};

		useImperativeHandle(ref, () => ({
			validateMappings,
		}));

		return (
			<Stack space="space.100">
				<Heading size="small">
					{fg('confluence-issue-terminology-refresh') ? (
						<FormattedMessage {...messages.bulkConfigureMapTableToIssueTitleIssueTermRefresh} />
					) : (
						<FormattedMessage {...messages.bulkConfigureMapTableToIssueTitle} />
					)}
				</Heading>
				<Text color="color.text.subtlest">
					{fg('confluence-issue-terminology-refresh') ? (
						<FormattedMessage
							{...messages.bulkConfigureMapTableToIssueDescriptionIssueTermRefresh}
						/>
					) : (
						<FormattedMessage {...messages.bulkConfigureMapTableToIssueDescription} />
					)}
				</Text>
				<Box padding="space.100">
					<Stack space="space.100">
						<Stack space="space.300">
							{unsavedColumnToFieldMappings?.map((mapping, index) => (
								<ColumnToIssueFieldMapping
									key={createIssueFieldMappingKey(mapping, index)}
									index={index}
									mapping={mapping}
									tableColumnOptions={tableColumnOptions}
									issueFieldOptions={issueFieldOptions}
									onMappingChange={handleMappingChange}
									isLoading={loadingIssueTypeFields || isLoading}
									isDisabled={loadingIssueTypeFields || isLoading}
									pageMode={pageMode}
								/>
							))}
						</Stack>
					</Stack>
				</Box>
				{fg('confluence_bulk_issue_create_additional_mappings') && (
					<Box padding="space.0" paddingBlockStart="space.025">
						<Stack alignInline="start">
							<Button
								appearance="subtle"
								iconBefore={(iconProps) => <AddIcon {...iconProps} />}
								onClick={handleAddMappingClick}
								isDisabled={loadingIssueTypeFields || isLoading}
							>
								<FormattedMessage {...messages.bulkConfigureMapTableToIssueAddMapping} />
							</Button>
						</Stack>
					</Box>
				)}
			</Stack>
		);
	},
);
